/**
 * `<McpAccessPanel>` — the "MCP Access" tab under Settings → Authentication.
 *
 * Mints a personal MCP access token via `POST /api/auth/mcp-token` and
 * shows it ONCE (mirrors `<NewKeyDisplayDialog>`'s show-once contract:
 * navigating away drops the token from React state; it is never
 * recoverable from the dashboard afterwards). Tokens are stateless and
 * TTL-bounded (`MCP_TOKEN_TTL_SECONDS`, default 7 days) — generating a
 * new one does not revoke earlier ones, they simply expire.
 */

import { authClient } from "@cometchat-ai/auth/client";
import { Button, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Copy, KeyRound, Loader2, TriangleAlert } from "./icons";

const MCP_SERVER_URL = "https://mcp.crswrd.ai/mcp";
const TOKEN_TTL_DAYS = 7;

function mcpAddSnippet(token: string): string {
  return `claude mcp add --transport http crossword ${MCP_SERVER_URL} --header "Authorization: Bearer ${token}"`;
}

export function McpAccessPanel() {
  const { t } = useTranslation("mcp-access");
  const [token, setToken] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => authClient.mintMcpToken(),
    onSuccess: (result) => setToken(result.token),
    onError: () => toast.error(t("generateFailed")),
  });

  async function copyText(text: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      // Clipboard access may be denied in a sandbox; fail silently — the
      // user can still select the text manually.
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4" data-testid="mcp-access-panel">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-foreground">{t("heading")}</h2>
        <p className="text-[13px] text-muted-foreground">{t("description")}</p>
      </div>

      <Button
        size="sm"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
        data-testid="mcp-access-generate"
      >
        {mutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {mutation.isPending ? t("generating") : t("generate")}
      </Button>

      {token !== null ? (
        <div className="space-y-3">
          <div
            className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px]"
            style={{
              color: "var(--color-danger)",
              background: "color-mix(in oklch, var(--color-danger) 10%, transparent)",
            }}
          >
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t("warning")}</span>
          </div>

          <div className="flex min-w-0 items-stretch gap-2">
            <code
              className="min-w-0 flex-1 break-all rounded-lg bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-3 py-2 font-mono text-xs leading-relaxed text-foreground"
              data-testid="mcp-access-token"
            >
              {token}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void copyText(token, t("tokenCopiedToast"))}
              aria-label={t("copyToken")}
              className="h-auto shrink-0 gap-1.5 rounded-md text-[13px] hover:bg-muted"
              data-testid="mcp-access-copy-token"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              {t("copyToken")}
            </Button>
          </div>

          <div className="space-y-1.5">
            <h3 className="text-[13px] font-medium text-foreground">{t("snippetHeading")}</h3>
            <p className="text-[13px] text-muted-foreground">{t("snippetDescription")}</p>
            <div className="flex min-w-0 items-stretch gap-2">
              <code
                className="min-w-0 flex-1 break-all rounded-lg bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-3 py-2 font-mono text-xs leading-relaxed text-foreground"
                data-testid="mcp-access-snippet"
              >
                {mcpAddSnippet(token)}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void copyText(mcpAddSnippet(token), t("snippetCopiedToast"))}
                aria-label={t("copySnippet")}
                className="h-auto shrink-0 gap-1.5 rounded-md text-[13px] hover:bg-muted"
                data-testid="mcp-access-copy-snippet"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                {t("copySnippet")}
              </Button>
            </div>
          </div>

          <p className="text-[13px] text-muted-foreground">
            {t("expiryNote", { days: TOKEN_TTL_DAYS })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
